import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomButton } from '../../../../../UiComponentsLibrary/CustomButton';
import { BULK_EDITABLE_COLUMNS } from '../../config/bulkEditableColumns';
import { BulkBarContainer, BulkBarText, BulkBarSelect } from './BulkEditBar.styles';

export interface BulkEditSelection {
  columnKey: string;
  value: string;
}

interface BulkEditBarProps {
  selectedCount: number;
  onApply: (selection: BulkEditSelection) => void;
  onClear: () => void;
  isPending: boolean;
}

/**
 * Bulk action bar shown above the bookings grid (super admin, active view) when one or more rows
 * are selected. The user first picks which column to bulk-edit, then a value for that column, then
 * applies it to all selected bookings. Editable columns come from BULK_EDITABLE_COLUMNS, so adding
 * a new bulk-editable column needs no change here.
 */
const BulkEditBar: React.FC<BulkEditBarProps> = ({
  selectedCount,
  onApply,
  onClear,
  isPending,
}) => {
  const { t } = useTranslation();
  const [columnKey, setColumnKey] = useState('');
  const [value, setValue] = useState('');

  const selectedColumn = useMemo(
    () => BULK_EDITABLE_COLUMNS.find((col) => col.key === columnKey),
    [columnKey]
  );

  const handleColumnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setColumnKey(event.target.value);
    // Reset the value whenever the target column changes so a stale value can't be applied.
    setValue('');
  };

  return (
    <BulkBarContainer id="bulk-edit-bar">
      <BulkBarText>
        {t('bulk-gtm-selected-count', { count: selectedCount })}
      </BulkBarText>

      <BulkBarSelect
        id="bulk-edit-column-select"
        aria-label={t('bulk-edit-column-select-label')}
        value={columnKey}
        onChange={handleColumnChange}
      >
        <option value="">{t('bulk-edit-column-placeholder')}</option>
        {BULK_EDITABLE_COLUMNS.map((col) => (
          <option key={col.key} value={col.key}>
            {t(col.label)}
          </option>
        ))}
      </BulkBarSelect>

      <BulkBarSelect
        id="bulk-edit-value-select"
        aria-label={t('bulk-edit-value-select-label')}
        value={value}
        disabled={!selectedColumn}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value)}
      >
        <option value="">{t('bulk-edit-value-placeholder')}</option>
        {selectedColumn?.valueOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.label)}
          </option>
        ))}
      </BulkBarSelect>

      <CustomButton
        id="bulk-edit-apply-button"
        text={t('bulk-gtm-apply-button')}
        handleOnClickCb={() => onApply({ columnKey, value })}
        disabled={!columnKey || !value || isPending}
      />
      <CustomButton
        id="bulk-edit-clear-button"
        text={t('bulk-gtm-clear-button')}
        handleOnClickCb={onClear}
        disabled={isPending}
      />
    </BulkBarContainer>
  );
};

export default BulkEditBar;
